/**
 * Multi-select symptom toggles. FR-204.
 *
 * Grouped by category with a heading per group, because a flat list of thirteen
 * toggles is hard to scan when you feel bad — which is exactly when this screen
 * gets used.
 *
 * Each toggle is a real `aria-pressed` button rather than a styled checkbox, so
 * the pressed state is announced without depending on the visual fill.
 */
export interface SymptomOption {
  id: string
  label: string
  category: string
}

export interface SymptomTogglesProps {
  legend: string
  categoryLabel: (category: string) => string
  options: readonly SymptomOption[]
  selected: readonly string[]
  onToggle: (id: string) => void
}

export function SymptomToggles({
  legend,
  categoryLabel,
  options,
  selected,
  onToggle,
}: SymptomTogglesProps) {
  const categories = [...new Set(options.map((o) => o.category))]

  return (
    <fieldset>
      <legend className="pb-2 text-sm font-medium">{legend}</legend>
      {categories.map((category) => (
        <div key={category} className="pb-2">
          <p className="pb-1 text-xs text-ink-muted">{categoryLabel(category)}</p>
          <div className="flex flex-wrap gap-2">
            {options
              .filter((o) => o.category === category)
              .map((option) => {
                const on = selected.includes(option.id)
                return (
                  <button
                    key={option.id}
                    type="button"
                    aria-pressed={on}
                    onClick={() => onToggle(option.id)}
                    className={[
                      'min-h-11 rounded-full border px-3 text-xs transition-all duration-200 active:scale-[0.96]',
                      on
                        ? 'border-accent bg-accent text-accent-ink shadow-sm'
                        : 'border-line hover:bg-line/40 hover:border-ink-muted/30 text-ink',
                    ].join(' ')}
                  >
                    {option.label}
                  </button>
                )
              })}
          </div>
        </div>
      ))}
    </fieldset>
  )
}
